import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Barre de catégories défilable : swipe tactile, drag souris, flèches visibles (mobile + desktop),
// dégradés de bord indiquant le contenu masqué, centrage automatique de la catégorie active.
export function MenuCategoryBar({ children, activeKey }) {
  const ref = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const st = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, []);

  const prevKey = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || activeKey == null) return;
    const changed = prevKey.current !== null && prevKey.current !== activeKey;
    prevKey.current = activeKey;
    if (!changed) { el.scrollLeft = 0; return; } // à l'ouverture : 1ères rubriques (Plat du jour) visibles
    const btn = el.querySelector('[data-active="true"]');
    if (!btn) return;
    const left = btn.offsetLeft, right = left + btn.offsetWidth;
    const visL = el.scrollLeft + 36, visR = el.scrollLeft + el.clientWidth - 36;
    if (left >= visL && right <= visR) return; // déjà visible : ne pas décaler (les 1ères rubriques restent visibles à l'ouverture)
    const target = left - (el.clientWidth - btn.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeKey]);

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    if (e.pointerType && e.pointerType !== "mouse") return;
    const el = ref.current;
    if (!el) return;
    st.current = { down: true, startX: e.pageX, startLeft: el.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    const el = ref.current;
    if (!el || !st.current.down) return;
    const dx = e.pageX - st.current.startX;
    if (Math.abs(dx) > 4) st.current.moved = true;
    el.scrollLeft = st.current.startLeft - dx;
  };
  const endDrag = () => { st.current.down = false; };
  const onClickCapture = (e) => {
    if (st.current.moved) { e.preventDefault(); e.stopPropagation(); st.current.moved = false; }
  };

  const arrowCls = "grid place-items-center absolute top-[calc(50%-6px)] -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-[#C69C4A]/60 shadow-md text-[#7B1E24] hover:bg-[#FAF3E0] active:scale-95 transition-colors";

  return (
    <div className="relative max-w-6xl mx-auto" data-testid="category-bar">
      {canL && <div className="cat-fade-l" />}
      {canR && <div className="cat-fade-r" />}
      {canL && (
        <button type="button" aria-label="Catégories précédentes" data-testid="cat-scroll-left" onClick={() => nudge(-1)} className={`${arrowCls} left-1.5`}>
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
      )}
      <div
        ref={ref}
        data-testid="category-scroll"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        className="cat-scroll flex gap-2.5 px-4 pb-3 overflow-x-auto select-none cursor-grab active:cursor-grabbing"
      >
        {children}
        <span className="shrink-0 w-6" aria-hidden="true" />
      </div>
      {canR && (
        <button type="button" aria-label="Catégories suivantes" data-testid="cat-scroll-right" onClick={() => nudge(1)} className={`${arrowCls} right-1.5 cat-arrow-hint`}>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
